import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Phone, Mail, MapPin, Clock } from 'lucide-react';
import { FaFacebook, FaInstagram, FaWhatsapp, FaTiktok } from 'react-icons/fa';

const Footer = () => {
  const socialLinks = [
    { icon: FaFacebook, href: "http://facebook.com/Bienestaralnaturalmedellin", color: "hover:text-blue-500" },
    { icon: FaInstagram, href: "https://www.instagram.com/bienestaralnatural.medellin/", color: "hover:text-pink-500" },
    { 
      icon: FaWhatsapp, 
      href: `https://wa.me/573237076868?text=${encodeURIComponent("Hola, los vi en su website y quiero obtener mas información sobre sus servicios")}`, 
      color: "hover:text-green-500" 
    },
    { icon: FaTiktok, href: "https://www.tiktok.com/@bienestar.al.natu2?is_from_webapp=1&sender_device=pc", color: "hover:text-black" }
  ];

  const quickLinks = [
    { name: 'Inicio', href: '#home' },
    { name: 'Nosotros', href: '#about' },
    { name: 'Servicios', href: '#services' },
    { name: 'Galería', href: '#gallery' },
    { name: 'Contacto', href: '#contact' }
  ];

  const handleSmoothScroll = (e, href) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">

          {/* Logo y descripción */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 lg:gap-4">
              <img
                src="https://utfs.io/f/5BN0V4mlt4NUrOWP0qz5NPcaAgwizpdKqn4rb3Q1IHGDuV67"
                alt="Bienestar Al Natural Logo"
                className="w-14 h-14 lg:w-20 lg:h-20 rounded-full object-cover"
              />
              <div>
                <h3 className="text-xl lg:text-2xl font-bold">Bienestar al Natural</h3>
                <p className="text-green-400 text-sm lg:text-base">Salud y Belleza</p>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed text-sm lg:text-base">
              Comprometidos con tu bienestar a través de servicios especializados 
              que respetan el equilibrio de tu cuerpo y mente.
            </p>
            <div className="flex items-center gap-2 text-green-400">
              <Heart className="w-5 h-5" />
              <span className="font-medium text-sm lg:text-base">Hecho con amor para tu salud</span>
            </div>
          </motion.div>

          {/* Enlaces rápidos */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4 className="text-xl font-semibold text-green-400">Enlaces Rápidos</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <motion.a
                    href={link.href}
                    onClick={(e) => handleSmoothScroll(e, link.href)}
                    className="cursor-pointer text-gray-300 hover:text-green-400 transition-colors duration-300 text-sm lg:text-base"
                    whileHover={{ x: 5 }}
                  >
                    {link.name}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Información de contacto */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="text-xl font-semibold text-green-400">Contacto</h4>
            <div className="space-y-3">
              <a
                href={`https://wa.me/573237076868?text=${encodeURIComponent("Hola, los vi en su website y quiero obtener mas información sobre sus servicios")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3"
              >
                <Phone className="w-5 h-5 text-green-400" />
                <span className="text-gray-300 text-sm lg:text-base">3237076868</span>
              </a>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-green-400" />
                <span className="text-gray-300 text-sm lg:text-base">vidbienat@gmail.com</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-green-400" />
                <span className="text-gray-300 text-sm lg:text-base">
                  Cra 78A # 48 - 87, Medellín, Colombia
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-green-400" />
                <span className="text-gray-300 text-sm lg:text-base">
                  Lunes a Viernes: 8:00 AM - 4:00 PM
                </span>
              </div>
            </div>
          </motion.div>

          {/* Redes sociales */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h4 className="text-xl font-semibold text-green-400">Síguenos</h4>
            <p className="text-gray-300 text-sm lg:text-base">
              Mantente conectado con nosotros en nuestras redes sociales para tips de salud y belleza.
            </p>
            <div className="flex flex-wrap justify-start sm:justify-center gap-4 mt-2">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-300 ${social.color} text-white shadow hover:shadow-lg`}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Icon className="w-6 h-6" />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

        </div>

        {/* Copyright */}
        <motion.div
          className="border-t border-gray-700 mt-12 pt-8 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-400 text-sm lg:text-base">
            © 2025 Bienestar al Natural. Todos los derechos reservados. | 
            <span className="text-green-400"> Diseñado con amor para tu bienestar</span>
          </p>
        </motion.div>

      </div>
    </footer>
  );
};

export default Footer;



